import { ArrowRight, Check, CheckCircle2, RotateCcw, X } from "lucide-react";
import { useMemo, useState } from "react";
import { postJson } from "../lib/api";
import type { LessonDetail, QuizResult } from "../types/api";
import { useToast } from "../context/ToastContext";

export function Quiz({ lesson, onCompleted }: { lesson: LessonDetail; onCompleted: () => void }) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();
  const allAnswered = useMemo(() => lesson.quiz.questions.every((question) => answers[question.id] !== undefined), [answers, lesson.quiz.questions]);

  async function submitQuiz() {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const data = await postJson<QuizResult>(`/api/lessons/${lesson.id}/quiz`, { answers });
      setResult(data);
      if (data.passed) {
        showToast(`Quiz passed — ${data.score}%`);
        onCompleted();
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not submit the quiz.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  function retry() {
    setAnswers({});
    setResult(null);
    document.getElementById("progress-test")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <section className="quiz-card" id="progress-test">
      <div className="quiz-heading">
        <div><span className="eyebrow">Progress test</span><h2>Check what you learned</h2><p>Score {lesson.quiz.passingScore}% or higher to complete this lesson.</p></div>
        <span className="question-count">{lesson.quiz.questions.length} questions</span>
      </div>
      {lesson.quiz.questions.map((question, questionIndex) => {
        const questionResult = result?.results.find((item) => item.questionId === question.id);
        return (
          <fieldset key={question.id} className="quiz-question" disabled={Boolean(result)}>
            <legend><span>{String(questionIndex + 1).padStart(2, "0")}</span>{question.question}</legend>
            <div className="quiz-options">
              {question.options.map((option, optionIndex) => {
                const selected = answers[question.id] === optionIndex;
                const correctAfterSubmit = questionResult?.correctAnswer === optionIndex;
                const wrongAfterSubmit = Boolean(result && selected && !questionResult?.correct);
                return (
                  <label key={option} className={`quiz-option ${selected ? "selected" : ""} ${correctAfterSubmit ? "answer-correct" : ""} ${wrongAfterSubmit ? "answer-wrong" : ""}`}>
                    <input type="radio" name={question.id} checked={selected} onChange={() => setAnswers((current) => ({ ...current, [question.id]: optionIndex }))} />
                    <span className="option-letter">{String.fromCharCode(65 + optionIndex)}</span>
                    <span>{option}</span>
                    {correctAfterSubmit && <Check size={17} />}
                    {wrongAfterSubmit && <X size={17} />}
                  </label>
                );
              })}
            </div>
            {questionResult && <div className={`answer-explanation ${questionResult.correct ? "correct" : "incorrect"}`}><strong>{questionResult.correct ? "Correct" : "Not quite"}.</strong> {questionResult.explanation}</div>}
          </fieldset>
        );
      })}
      {result ? (
        <div className={`quiz-result ${result.passed ? "quiz-pass" : "quiz-retry"}`}>
          <div className="score-orbit"><strong>{result.score}%</strong><span>{result.passed ? "Passed" : "Keep going"}</span></div>
          <div><h3>{result.passed ? "Nice work — lesson complete!" : "Almost there."}</h3><p>{result.passed ? `Your best score is ${result.bestScore}%. Continue when you’re ready.` : `You need ${lesson.quiz.passingScore}% to pass. Review the explanations and try again.`}</p></div>
          <button className="button button-secondary" onClick={retry}><RotateCcw size={17} />Try again</button>
        </div>
      ) : (
        <div className="quiz-submit-row">
          <span>{Object.keys(answers).length} of {lesson.quiz.questions.length} answered</span>
          <button className="button button-primary" disabled={!allAnswered || submitting} onClick={submitQuiz}>{submitting ? "Checking answers…" : "Submit answers"}<ArrowRight size={17} /></button>
        </div>
      )}
    </section>
  );
}
